<script setup lang="ts">
import type { UIExpandableProps } from '../../types'
import { ref, computed } from '#imports'
import type { UIIcon } from '#components'

const emit = defineEmits([
    'collapsed',
    'expanded'
])

const props = withDefaults(defineProps<UIExpandableProps>(), {
    animationDuration: 300,
    disabled: false,
    showHeader: true,
    isOpen: undefined,
    showToggleIcon: false,
    renderClosed: false
})

const openState = ref(!props.showHeader)

const open = computed((): boolean => props.isOpen != null ? props.isOpen : openState.value)

function toggle() {
    if (props.disabled) {
        return
    }

    openState.value = !openState.value
}
/*
* We create our own expansion animation programmatically, because it depends on a variable-size height.
* This is not supported by CSS, as only explicitly set height (px etc) uses transitions.
*/
function expand(el: Element, done: () => void) {
    const htmlEl = el as HTMLElement
    const height = htmlEl.scrollHeight

    // Safari <13 does not support animate function, and also breaks when height is set to auto or specific number...
    // Unsetting the value seems to work along with instant expand/collapse
    if (!htmlEl.animate) {
        htmlEl.style.height = 'unset'

        emit('expanded')
        done()

        return
    }

    htmlEl.animate(
        [
            { height: '0px', overflowY: 'hidden' },
            { height: `${height}px`, overflowY: 'hidden' }
        ],
        {
            duration: props.animationDuration,
            easing: 'ease-in-out'
        }
    ).onfinish = () => {
        // After the animation has finished, we set height to auto,
        // to ensure the height can accurately update when content changes
        htmlEl.style.height = 'auto'
        emit('expanded')
        done()
    }
}

function collapse(el: Element, done: () => void) {
    const htmlEl = el as HTMLElement
    const height = htmlEl.scrollHeight

    // Old safari does not support animate function
    if (!htmlEl.animate) {
        htmlEl.style.height = '0px'
        emit('collapsed')
        done()

        return
    }

    htmlEl.animate(
        [
            { height: `${height}px`, overflowY: 'hidden' },
            { height: '0px', overflowY: 'hidden' }
        ],
        {
            duration: props.animationDuration,
            easing: 'ease-in-out'
        }
    ).onfinish = () => {
        emit('collapsed')
        done()
    }
}

defineExpose({
    toggle,
    open
})
</script>

<template>
    <div :class="['expandable', { 'expandable--open': open, 'expandable--disabled': disabled, 'expandable--with-toggle-icon': props.showToggleIcon }]">
        <div v-if="showHeader" class="expandable__header" @click="toggle">
            <slot name="header" />

            <template v-if="props.showToggleIcon">
                <UIIcon
                    ref="toggleIconRef"
                    name="arrow-down"
                    class="expandable__toggle-icon"
                    size="12px"
                />
            </template>
        </div>

        <Transition @enter="expand" @leave="collapse">
            <div
                v-if="props.renderClosed"
                v-show="open"
                ref="content"
                class="expandable__content"
            >
                <slot :open="open" />
            </div>
            <div v-else-if="open" ref="content" class="expandable__content">
                <slot :open="open" />
            </div>
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
.expandable {
    display: flex;
    flex-direction: column;

    &--open {
        transition: margin 0.3s ease-in-out;

        > .expandable__content {
            height: auto;
            margin-bottom: 15px;
        }

        .expandable__toggle-icon {
            transform: rotate(-180deg);
        }
    }

    &__header {
        cursor: pointer;
        position: sticky;
        top: 0;
        z-index: 1;

        white-space: wrap;
        word-wrap: break-word;
    }

    &__content {
        height: 0;
        overflow: hidden;
        z-index: 0;
    }

    &__toggle-icon {
        transition: transform 0.25s ease-in-out;
    }

    &--with-toggle-icon {
        .expandable__header {
            display: grid;
            grid-template-columns: minmax(0, 1fr) max-content;
            align-items: center;
            gap: 0.5rem;
        }
    }
}
</style>
